'use client'

import { useAction } from 'next-safe-action/hooks'
import { useRef } from 'react'

import { requestPassportOCR, TPassportOCRResponse } from '@/entities/auth'
import { CERT } from '@/shared/api'
import { useServerErrorHandler } from '@/shared/config/action'

const MAX_OCR_FAIL_COUNT = 3

type ActionProps = {
  onSuccess: (data: TPassportOCRResponse) => void
  onMaxFail: () => void
}

export const usePassportOCR = () => {
  const { handleServerError } = useServerErrorHandler()

  const failCountRef = useRef(0)
  const isCancelledRef = useRef(false)

  const action = useAction(requestPassportOCR)

  const cancel = () => {
    isCancelledRef.current = true
  }

  const requestOcrAsync = async (
    passportImageUrl: string,
    { onSuccess, onMaxFail }: ActionProps,
  ) => {
    isCancelledRef.current = false

    const result = await action.executeAsync({ passportImageUrl })

    if (isCancelledRef.current) return

    if (result?.serverError) {
      const { code } = result.serverError

      const isOCRFailure =
        code === CERT['003'] || code === CERT['005'] || code === CERT['006']

      if (isOCRFailure) {
        failCountRef.current += 1
      }

      if (failCountRef.current >= MAX_OCR_FAIL_COUNT) {
        onMaxFail()
      } else {
        handleServerError(result.serverError)
      }

      return
    }

    if (result?.data) {
      onSuccess(result.data)
    }
  }

  return { requestOcrAsync, cancel, isScanning: action.isExecuting }
}
